import { Table, Button, Tooltip, Row, Switch, message, Modal, Select } from 'antd';
import { EditOutlined, DeleteOutlined, LogoutOutlined, StarFilled } from '@ant-design/icons';
import type { TableProps } from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { Team, TabKey } from '../types';
import { team_updatestatusteam, team_updatestatususer, team_updateTaskRole } from '../../../services/team';

interface TeamTableProps {
  teams: Team[];
  tabKey: TabKey;
  taskId: string;
  onEdit: (team: Team) => void;
  onDelete: (id: string) => void;
  onLeave: (id: string) => void;
  onRemoveMember?: (teamId: string, userId: string) => void;
  onRowClick: (team: Team) => void;
  onStatusChange: (teamId: string, newStatus: number) => void;
  onRefresh?: () => void;
  canDeleteTeam: (team: Team) => boolean;
  canEditTeam: (team: Team) => boolean;
  canLeaveTeam: (team: Team) => boolean;
  currentUser?: any;
  isTaskAdmin?: boolean;
}

export const TeamTable: React.FC<TeamTableProps> = ({
  teams,
  tabKey,
  taskId,
  onEdit,
  onDelete,
  onLeave,
  onRemoveMember,
  onRowClick,
  onStatusChange,
  onRefresh,
  canDeleteTeam,
  canEditTeam,
  canLeaveTeam,
  currentUser,
  isTaskAdmin,
}) => {
  const isSuperAdmin = currentUser?.role_id === '99999999-0000-0000-0000-999999999999';
  const isAdminOrSuperAdmin = isSuperAdmin || !!isTaskAdmin;

  const [editRoleModal, setEditRoleModal] = useState<{ open: boolean; record: Team | null; role: number }>({
    open: false,
    record: null,
    role: 1,
  });
  const [updatingRole, setUpdatingRole] = useState(false);

  const handleEditTaskRole = (record: Team) => {
    const currentRole = record.members[0]?.task_role ?? 1;
    setEditRoleModal({ open: true, record, role: currentRole });
  };

  const handleSaveTaskRole = async () => {
    if (!editRoleModal.record) return;
    setUpdatingRole(true);
    try {
      await team_updateTaskRole({
        task_id: taskId,
        user_id: editRoleModal.record.id,
        role: editRoleModal.role,
      });
      message.success('อัพเดท task role สำเร็จ');
      setEditRoleModal({ open: false, record: null, role: 1 });
      onRefresh?.();
    } catch (error) {
      message.error('ไม่สามารถอัพเดท task role ได้');
    } finally {
      setUpdatingRole(false);
    }
  };
  const handleTeamStatusChange = async (team: Team, checked: boolean) => {
    const newStatus = checked ? 1 : 2;
    onStatusChange(team.id, newStatus);
    try {
      await team_updatestatusteam({
        task_id: taskId,
        team_id: team.id,
        status: newStatus.toString(),
      });
      message.success('อัพเดท status สำเร็จ');
    } catch (error) {
      message.error('ไม่สามารถอัพเดท status ได้');
      // Revert กลับถ้า error
      onStatusChange(team.id, checked ? 2 : 1);
    }
  };

  const handleUserStatusChange = async (record: Team, checked: boolean) => {
    const newStatus = checked ? 1 : 2;
    const userId = record.id;
    onStatusChange(record.id, newStatus);
    try {
      await team_updatestatususer({
        task_id: taskId,
        user_id: userId,
        status: newStatus.toString(),
      });
      message.success('อัพเดท status สำเร็จ');
    } catch (error) {
      message.error('ไม่สามารถอัพเดท status ได้');
      onStatusChange(record.id, checked ? 2 : 1);
    }
  };
  // Table columns definition for All Member tab
  const memberColumns: TableProps<Team>['columns'] = [
    {
      title: <div style={{ fontSize: '18px', textAlign: 'center' }}>Name</div>,
      dataIndex: 'name',
      key: 'name',
      width: 300,
    },
    {
      title: <div style={{ fontSize: '18px', textAlign: 'center' }}>Email</div>,
      key: 'email',
      align: 'center',
      render: (_: any, record: Team) => {
        return record.members[0]?.email || 'N/A';
      },
    },
    {
      title: <div style={{ fontSize: '18px', textAlign: 'center' }}>Team Name</div>,
      key: 'team_name',
      align: 'center',
      render: (_: any, record: Team) => {
        return (record as any).team_name || '-';
      },
    },
    {
      title: <div style={{ fontSize: '18px', textAlign: 'center' }}>Team Role</div>,
      key: 'team_role',
      align: 'center',
      render: (_: any, record: Team) => {
        const role = record.members[0]?.role;
        if (role === undefined || role === null) return '-';
        return role === 1 ? 'Leader' : 'Member';
      },
    },
    ...(isAdminOrSuperAdmin ? [{
      title: <div style={{ fontSize: '18px', textAlign: 'center' }}>Task Role</div>,
      key: 'task_role',
      align: 'center' as const,
      render: (_: any, record: Team) => {
        const taskRole = record.members[0]?.task_role;
        const label = taskRole === undefined || taskRole === null ? '-' : taskRole === 2 ? 'Admin' : 'Member';
        return (
          <Row justify="center" align="middle" wrap={false} style={{ gap: 6 }}>
            <span>{label}</span>
            {isAdminOrSuperAdmin && (
              <Tooltip title="Edit Task Role">
                <Button
                  size="small"
                  icon={<EditOutlined style={{ color: '#FFB84C' }} />}
                  style={{ backgroundColor: '#fff4cc', border: 'none' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditTaskRole(record);
                  }}
                />
              </Tooltip>
            )}
          </Row>
        );
      },
    }] : []),
    ...(isSuperAdmin ? [{
      title: <div style={{ fontSize: '18px', textAlign: 'center' }}>Active</div>,
      key: 'Active',
      align: 'center' as const,
      render: (_: any, record: Team) => (
        <Switch
          checked={record.status === 1}
          checkedChildren="เปิด"
          unCheckedChildren="ปิด"
          onChange={(checked) => handleUserStatusChange(record, checked)}
        />
      ),
    }] : []),
    ...(isAdminOrSuperAdmin && onRemoveMember ? [{
      title: '',
      key: 'action',
      align: 'center' as const,
      render: (_: any, record: Team) => {
        const teamId = (record as any).team_id ?? '';
        return (
          <Tooltip title="Remove from task">
            <Button
              icon={<DeleteOutlined style={{ color: '#B31312' }} />}
              style={{ backgroundColor: '#FFDFDF' }}
              onClick={(e) => {
                e.stopPropagation();
                onRemoveMember(teamId, record.id);
              }}
            />
          </Tooltip>
        );
      },
    }] : []),
  ];

  // Table columns definition for Team tabs
  const teamColumns: TableProps<Team>['columns'] = [
    {
      title: '',
      key: 'my_team',
      width: 100,
      align: 'center',
      render: (_: any, record: Team) => {
        const isMyTeam = record.members.some((m) => (m.user_id || m.id) === currentUser?.user_id);
        return isMyTeam ? <Tooltip title="My Team"><StarFilled style={{ color: '#1677ff', fontSize: 18 }} /></Tooltip> : null;
      },
    },
    {
      title: <div style={{ fontSize: '18px', textAlign: 'center' }}>Team Name</div>,
      dataIndex: 'name',
      key: 'name',
      width: 300,
    },
    {
      title: <div style={{ fontSize: '18px', textAlign: 'center' }}>Leader Email</div>,
      key: 'leader',
      align: 'center',
      render: (_: any, record: Team) => {
        const leader = record.members.find((m) => m.role === 1);
        return leader?.email || 'N/A';
      },
    },
    {
      title: <div style={{ fontSize: '18px', textAlign: 'center' }}>Total Members</div>,
      key: 'members_count',
      align: 'center',
      render: (_: any, record: Team) => record.members.length,
    },
    {
      title: <div style={{ fontSize: '18px', textAlign: 'center' }}>Lasted update</div>,
      dataIndex: 'createdAt',
      key: 'createdAt',
      align: 'center',
      render: (date: string) => dayjs(date).format('DD/MM/YYYY'),
    },
    // แสดง Active column เฉพาะ Super Admin เท่านั้น
    ...(isSuperAdmin ? [{
      title: <div style={{ fontSize: '18px', textAlign: 'center' }}>Active</div>,
      key: 'Active',
      align: 'center' as const,
      render: (_: any, record: Team) => (
        <Switch
          checked={record.status === 1}
          checkedChildren="เปิด"
          unCheckedChildren="ปิด"
          onChange={(checked) => handleTeamStatusChange(record, checked)}
        />
      ),
    }] : []),
    {
      title: '',
      key: 'action',
      align: 'center',
      render: (_: any, record: Team) => {
        const canDelete = canDeleteTeam(record);
        const canEdit = canEditTeam(record);
        const canLeave = canLeaveTeam(record);
        return (
          <Row justify="center" align="middle">
            {canEdit && (
              <Tooltip title="Edit">
                <Button
                  // size="small"
                  icon={<EditOutlined style={{ color: '#FFB84C' }} />}
                  style={{ marginRight: 8, backgroundColor: '#fff4cc' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(record);
                  }}
                />
              </Tooltip>
            )}
            {canLeave && (
              <Tooltip title="Leave Team">
                <Button
                  // size="small"
                  icon={<LogoutOutlined style={{ color: '#FF6B00' }} />}
                  style={{ marginRight: 8, backgroundColor: '#FFE5CC' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onLeave(record.id);
                  }}
                />
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip title="Delete">
                <Button
                  // size="small"
                  icon={<DeleteOutlined style={{ color: '#B31312' }} />}
                  style={{ backgroundColor: '#FFDFDF' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(record.id);
                  }}
                />
              </Tooltip>
            )}
          </Row>
        );
      },
    },
  ];

  const columns = tabKey === 'user' ? memberColumns : teamColumns;

  return (
    <>
    <Modal
      title="แก้ไข Task Role"
      open={editRoleModal.open}
      onOk={handleSaveTaskRole}
      onCancel={() => setEditRoleModal({ open: false, record: null, role: 1 })}
      confirmLoading={updatingRole}
      okText="บันทึก"
      cancelText="ยกเลิก"
    >
      <div style={{ marginBottom: 8 }}>
        <span>ผู้ใช้: <strong>{editRoleModal.record?.name}</strong></span>
      </div>
      <Select
        style={{ width: '100%' }}
        value={editRoleModal.role}
        onChange={(val) => setEditRoleModal((prev) => ({ ...prev, role: val }))}
        options={[
          { label: 'Member', value: 1 },
          { label: 'Admin', value: 2 },
        ]}
      />
    </Modal>
    <Table
      columns={columns}
      dataSource={teams.map((team) => ({ ...team, key: team.id }))}
      onRow={(record) => ({
        onClick: () => {
          if (record.members.length > 0 && tabKey !== 'user') {
            onRowClick(record);
          }
        },
        style: {
          cursor: record.members.length > 0 && tabKey !== 'user' ? 'pointer' : 'default',
        },
      })}
      pagination={{
        showTotal: (total, range) =>
          `แสดงรายการต่อหน้า : ${range[0]}-${range[1]} จาก ${total} รายการ`,
        defaultPageSize: 10,
        showSizeChanger: false,
      }}
      scroll={{ x: 'max-content' }}
    />
    </>
  );
};
